import {
  Box,
  Flex,
  Group,
  Image,
  Modal,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconFile,
  IconFileMusic,
  IconFileText,
  IconFileZip,
  IconTrash,
  IconVideo,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-react";
import { Fragment, memo, useCallback, useState } from "react";
import ConfirmationButton from "src/components/shared/ConfirmationButton/ConfirmationButton";
import { DateTimeDisp } from "src/components/shared/DateTime";
import Loader from "src/components/shared/Loader/Loader";
import { usePostApi, useTheme } from "src/hooks";
import { type IFile } from "src/interfaces";
import { useDispatch } from "src/redux/hooks";
import { removeFile } from "src/redux/slices/generatorSlice";
import { openBase64 } from "src/utils";

import styles from "./Gallery.module.scss";
import { useGalleryFiles } from "./useGalleryFiles";

interface Props {
  files?: IFile[];
  readonly?: boolean;
  onRemove?: (attachmentId: string) => void | Promise<void>;
  customHandleRemove?: (attachmentId: string) => Promise<void>;
}

const getFileIcon = (filetype: string, filename?: string) => {
  const type = filetype?.toLowerCase() || "";
  const name = filename?.toLowerCase() || "";

  // Check if it's an image by type
  if (
    type.includes("image") ||
    type === "jpg" ||
    type === "jpeg" ||
    type === "png" ||
    type === "avif"
  )
    return null;

  // Check if attachment with image extension
  if (
    type === "attachment" &&
    (name.endsWith(".jpg") ||
      name.endsWith(".jpeg") ||
      name.endsWith(".png") ||
      name.endsWith(".avif"))
  ) {
    return null;
  }

  if (type.includes("pdf") || type.includes("document"))
    return <IconFile size="2rem" />;
  if (type.includes("text")) return <IconFileText size="2rem" />;
  if (type.includes("video")) return <IconVideo size="2rem" />;
  if (type.includes("audio") || type.includes("music"))
    return <IconFileMusic size="2rem" />;
  if (type.includes("zip") || type.includes("archive"))
    return <IconFileZip size="2rem" />;

  return <IconFile size="2rem" />;
};

const isImageFile = (filetype: string, filename?: string): boolean => {
  const type = filetype?.toLowerCase() || "";
  const name = filename?.toLowerCase() || "";

  const isImageType =
    type.includes("image") ||
    type === "jpg" ||
    type === "jpeg" ||
    type === "png" ||
    type === "avif" ||
    type === "attachment";

  // Check by filename extension
  const hasImageExtension =
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".png") ||
    name.endsWith(".avif");

  return isImageType || hasImageExtension;
};

const Gallery = ({
  files = [],
  readonly = false,
  onRemove,
  customHandleRemove,
}: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const filesWithData = useGalleryFiles(files);
  const allFiles = filesWithData;

  const borderColor = theme.isDarkMode
    ? theme.colors.main[9]
    : theme.colors.inputBorder[0];

  // Image viewer modal state
  const [selectedImage, setSelectedImage] = useState<{
    src: string;
    filename: string;
    date?: string;
    dateUTC?: string;
  } | null>(null);
  const [zoom, setZoom] = useState(1);

  const { post: viewFile } = usePostApi();
  const { post } = usePostApi();

  const handleRemoveFile = useCallback(
    async (file: IFile) => {
      if (customHandleRemove && file.aid) {
        await customHandleRemove(String(file.aid));
        dispatch(removeFile(file.aid));
      } else if (onRemove && file.filename) {
        await onRemove(file.filename);
      } else if (file.aid) {
        try {
          await post({
            request: "remove-file",
            body: { params: { aid: file.aid } },
          });
          dispatch(removeFile(file.aid));
        } catch (error) {
          console.error("Error removing file:", error);
        }
      }
    },
    [customHandleRemove, onRemove, post, dispatch],
  );

  const handleViewFile = useCallback(
    async (file: IFile) => {
      if (!file.aid) return;

      try {
        const response = await viewFile({
          request: "view-file",
          body: { params: { aid: file.aid } },
        });

        if (response?.data) {
          openBase64(response.data, response.type || file.type, file.filename);
        }
      } catch (error) {
        console.error("Error viewing file:", error);
      }
    },
    [viewFile],
  );

  if (allFiles.length === 0) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: "200px" }}>
        <Text c="dimmed">No files available</Text>
      </Flex>
    );
  }

  return (
    <Fragment>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: "1rem",
          padding: "1rem",
        }}>
        {allFiles.map(file => {
          const filename = file.filename ?? file.name ?? "Unknown";
          const isImage = isImageFile(file.filetype, filename);
          const fileIcon = getFileIcon(file.filetype, filename);

          const getImageSrc = (): string => {
            const data = file.b64 || file.fetchedData || "";

            if (data.startsWith("data:")) {
              return data;
            }

            const ext = filename.split(".").pop()?.toLowerCase();
            const imageType =
              ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "avif"
                ? ext
                : "jpeg";
            return `data:image/${imageType};base64,${data}`;
          };

          return (
            <Box
              key={file.aid}
              style={{
                border: `1px solid ${borderColor}`,
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: theme.colors.background[0],
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}>
              <Box
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "150px",
                  backgroundColor: theme.colors.background[0],
                  position: "relative",
                  opacity: file.deleted ? 0.6 : 1,
                }}>
                {!readonly && !file.deleted && file.aid && (
                  <Box
                    style={{
                      position: "absolute",
                      top: "0.5rem",
                      right: "0.5rem",
                      zIndex: 10,
                    }}>
                    <ConfirmationButton
                      actionIcon
                      onConfirm={() => handleRemoveFile(file)}
                      modalText={`Are you sure you want to remove ${filename}?`}>
                      <Tooltip label="Remove">
                        <IconTrash color="#ff6b6b" />
                      </Tooltip>
                    </ConfirmationButton>
                  </Box>
                )}
                {file.deleted ? (
                  <Flex align="center" justify="center" direction="column">
                    {fileIcon}
                    <Text size="xs" c="dimmed" mt="xs" ta="center">
                      Removed
                    </Text>
                  </Flex>
                ) : isImage ? (
                  <>
                    {file.fetchError ? (
                      <Flex
                        align="center"
                        justify="center"
                        style={{ flex: 1, width: "100%" }}>
                        <Text size="sm" c="red" ta="center">
                          Failed to load
                        </Text>
                      </Flex>
                    ) : file.url ? (
                      <Image
                        src={file.url}
                        alt={filename}
                        fit="cover"
                        height="150px"
                        width="100%"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setZoom(1);
                          setSelectedImage({
                            src: file.url!,
                            filename,
                            date: file.date,
                            dateUTC: file.dateUTC,
                          });
                        }}
                      />
                    ) : file.b64 || file.fetchedData ? (
                      <Image
                        src={getImageSrc()}
                        alt={filename}
                        fit="cover"
                        height="150px"
                        width="100%"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setZoom(1);
                          setSelectedImage({
                            src: getImageSrc(),
                            filename,
                            date: file.date,
                            dateUTC: file.dateUTC,
                          });
                        }}
                      />
                    ) : (
                      <Flex
                        align="center"
                        justify="center"
                        style={{ flex: 1, width: "100%" }}>
                        <Text size="sm" c="dimmed" ta="center">
                          <Flex
                            align="center"
                            justify="center"
                            direction="column">
                            <Loader />
                          </Flex>
                        </Text>
                      </Flex>
                    )}
                  </>
                ) : (
                  <Flex
                    align="center"
                    justify="center"
                    direction="column"
                    style={{ cursor: "pointer", height: "100%", width: "100%" }}
                    onClick={() => handleViewFile(file)}>
                    {fileIcon}
                    <Text size="xs" c="dimmed" mt="xs" ta="center">
                      {file.type || "File"}
                    </Text>
                  </Flex>
                )}
              </Box>

              <Stack
                gap={2}
                style={{
                  padding: "0.75rem",
                  borderTop: `1px solid ${borderColor}`,
                }}>
                {(file.date || file.dateUTC) && (
                  <DateTimeDisp
                    dir="row"
                    utc={file.dateUTC}
                    lt={file.date}
                    size="xs"
                    gap={2}
                  />
                )}
                <Tooltip label={filename}>
                  <Text
                    size="sm"
                    fw={500}
                    truncate
                    style={{ cursor: "default" }}>
                    {filename}
                  </Text>
                </Tooltip>
              </Stack>
            </Box>
          );
        })}
      </div>

      {/* Image Viewer Modal */}
      <Modal
        opened={selectedImage !== null}
        onClose={() => setSelectedImage(null)}
        title={
          <Flex gap={8} align="center">
            {selectedImage?.filename}
            {(selectedImage?.date || selectedImage?.dateUTC) && (
              <DateTimeDisp
                dir="row"
                utc={selectedImage?.dateUTC}
                lt={selectedImage?.date}
                gap={2}
                size="14px"
              />
            )}
          </Flex>
        }
        size="80%"
        centered>
        {selectedImage && (
          <Flex direction="column" align="center" gap="md">
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "70vh",
                overflow: "auto",
                border: `1px solid ${borderColor}`,
                borderRadius: "4px",
                backgroundColor: theme.colors.background[0],
              }}>
              <img
                src={selectedImage.src}
                alt={selectedImage.filename}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  transform: `scale(${zoom})`,
                  transition: "transform 0.2s ease",
                  transformOrigin: "center center",
                }}
              />
            </Box>

            <Group gap="md">
              <Tooltip label="Zoom In">
                <Box
                  component="button"
                  className={styles.controlButton}
                  onClick={() => setZoom(prev => Math.min(prev + 0.2, 3))}>
                  <IconZoomIn size="1.2rem" color={theme.colors.main[9]} />
                </Box>
              </Tooltip>

              <Text
                size="sm"
                fw={500}
                style={{ minWidth: "60px", textAlign: "center" }}>
                {Math.round(zoom * 100)}%
              </Text>

              <Tooltip label="Zoom Out">
                <Box
                  component="button"
                  className={styles.controlButton}
                  onClick={() => setZoom(prev => Math.max(prev - 0.2, 0.5))}>
                  <IconZoomOut size="1.2rem" color={theme.colors.main[9]} />
                </Box>
              </Tooltip>

              <Tooltip label="Reset Zoom">
                <Box
                  component="button"
                  className={styles.resetButton}
                  onClick={() => setZoom(1)}>
                  <Text size="sm">Reset</Text>
                </Box>
              </Tooltip>
            </Group>
          </Flex>
        )}
      </Modal>
    </Fragment>
  );
};

export default memo(Gallery);
